import type { ReactNode } from 'react';

import { ChevronDown, RotateCcw } from 'lucide-react';
import { useState } from 'react';

import type { VadSectionDefinition, VadValues } from './vad-config';

import { VadParameterField } from './vad-parameter-field';

interface VadSectionProps {
  readonly definition: VadSectionDefinition;
  readonly values: VadValues;
  readonly onValueChange: (key: string, value: number) => void;
  readonly onReset: (keys: readonly string[]) => void;
  readonly icon: ReactNode;
}

export function VadSection({
  definition,
  values,
  onValueChange,
  onReset,
  icon,
}: VadSectionProps): React.JSX.Element {
  const [open, setOpen] = useState<boolean>(true);
  const contentId = `vad-section-${definition.id}`;

  return (
    <section className="rounded-xl border bg-card">
      <div className="flex items-center gap-2 p-4">
        <button
          type="button"
          onClick={() => setOpen(current => !current)}
          aria-expanded={open}
          aria-controls={contentId}
          className="flex min-h-11 flex-1 items-center gap-3 rounded-lg text-left"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-muted-foreground">
            {icon}
          </span>
          <span className="flex flex-col">
            <span className="text-sm font-semibold text-foreground">{definition.title}</span>
            <span className="text-xs text-muted-foreground">{definition.description}</span>
          </span>
          <ChevronDown
            className={`ml-auto h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </button>
        <button
          type="button"
          onClick={() => onReset(definition.parameters.map(parameter => parameter.key))}
          className="flex h-11 shrink-0 items-center gap-2 rounded-lg border bg-background px-3 text-xs text-muted-foreground transition-colors hover:bg-muted"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Réinitialiser
        </button>
      </div>

      {open && (
        <div id={contentId} className="flex flex-col gap-6 border-t p-4">
          {definition.parameters.map(parameter => (
            <VadParameterField
              key={parameter.key}
              definition={parameter}
              value={values[parameter.key] ?? parameter.defaultValue}
              onChange={value => onValueChange(parameter.key, value)}
            />
          ))}
        </div>
      )}
    </section>
  );
}
